import { useState } from "react";
import { Dialog } from "@mui/material";
import styled from "styled-components";
import {
  IssueActionStatus,
  issueActionStatusLabel,
  IssueResponsibility,
  issueResponsibilityLabel,
  IssueType,
  issueTypeLabel,
} from "../../types/delivery-issue/delivery-issue";
import {
  useCreateDeliveryIssueMutation,
  useDeleteDeliveryIssueMutation,
  useDeliveryIssueListQuery,
  useUpdateDeliveryIssueMutation,
} from "../../query/delivery-issue-query";

interface Props {
  deliveryId: string | null;
  open: boolean;
  onClose: () => void;
}

const DEFAULT_FORM = {
  issueType: IssueType.Delay,
  responsibility: IssueResponsibility.Unknown,
  actionStatus: IssueActionStatus.InProgress,
  memo: "",
};

const DeliveryIssueDialog: React.FC<Props> = ({
  deliveryId,
  open,
  onClose,
}) => {
  const [form, setForm] = useState(DEFAULT_FORM);
  const listQuery = useDeliveryIssueListQuery(deliveryId, open);
  const createMutation = useCreateDeliveryIssueMutation(deliveryId);
  const updateMutation = useUpdateDeliveryIssueMutation(deliveryId);
  const deleteMutation = useDeleteDeliveryIssueMutation(deliveryId);

  const items = listQuery.data?.data?.items ?? [];

  const handleSubmit = () => {
    if (!deliveryId) return;
    createMutation.mutate(
      {
        deliveryId,
        issueType: form.issueType,
        responsibility: form.responsibility,
        actionStatus: form.actionStatus,
        memo: form.memo || undefined,
      },
      { onSuccess: () => setForm(DEFAULT_FORM) },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <Surface>
        <Title>이슈 등록 / 관리</Title>

        <FormGrid>
          <Field>
            <Label>이슈 유형</Label>
            <Select
              value={form.issueType}
              onChange={(e) =>
                setForm({ ...form, issueType: e.target.value as IssueType })
              }
            >
              {Object.values(IssueType).map((t) => (
                <option key={t} value={t}>
                  {issueTypeLabel[t]}
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            <Label>귀책</Label>
            <Select
              value={form.responsibility}
              onChange={(e) =>
                setForm({
                  ...form,
                  responsibility: e.target.value as IssueResponsibility,
                })
              }
            >
              {Object.values(IssueResponsibility).map((r) => (
                <option key={r} value={r}>
                  {issueResponsibilityLabel[r]}
                </option>
              ))}
            </Select>
          </Field>
          <Field>
            <Label>조치 상태</Label>
            <Select
              value={form.actionStatus}
              onChange={(e) =>
                setForm({
                  ...form,
                  actionStatus: e.target.value as IssueActionStatus,
                })
              }
            >
              {Object.values(IssueActionStatus).map((a) => (
                <option key={a} value={a}>
                  {issueActionStatusLabel[a]}
                </option>
              ))}
            </Select>
          </Field>
          <FieldFull>
            <Label>메모</Label>
            <Textarea
              rows={3}
              value={form.memo}
              onChange={(e) => setForm({ ...form, memo: e.target.value })}
              placeholder="조치 내용 / 자유 입력"
            />
          </FieldFull>
        </FormGrid>

        <Actions>
          <PrimaryButton
            onClick={handleSubmit}
            disabled={!deliveryId || createMutation.isLoading}
          >
            이슈 등록
          </PrimaryButton>
          <SecondaryButton onClick={onClose}>닫기</SecondaryButton>
        </Actions>

        <Divider />

        <SubTitle>등록된 이슈 ({items.length}건)</SubTitle>
        <List>
          {items.length === 0 && <Empty>등록된 이슈가 없습니다.</Empty>}
          {items.map((item) => (
            <ListItem key={item.id}>
              <Row>
                <Chip $tone="type">{issueTypeLabel[item.issueType]}</Chip>
                <Chip $tone="resp">
                  {issueResponsibilityLabel[item.responsibility]}
                </Chip>
                <StatusSelect
                  value={item.actionStatus}
                  onChange={(e) =>
                    updateMutation.mutate({
                      id: item.id,
                      payload: {
                        actionStatus: e.target.value as IssueActionStatus,
                      },
                    })
                  }
                >
                  {Object.values(IssueActionStatus).map((a) => (
                    <option key={a} value={a}>
                      {issueActionStatusLabel[a]}
                    </option>
                  ))}
                </StatusSelect>
                <DeleteBtn onClick={() => deleteMutation.mutate(item.id)}>
                  삭제
                </DeleteBtn>
              </Row>
              {item.memo && <Memo>{item.memo}</Memo>}
              <Timestamp>{new Date(item.createdAt).toLocaleString()}</Timestamp>
            </ListItem>
          ))}
        </List>
      </Surface>
    </Dialog>
  );
};

export default DeliveryIssueDialog;

const Surface = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 28px 32px;
  background: #ffffff;
`;

const Title = styled.h2`
  font-size: 18px; font-weight: 600; line-height: 26px;
  color: ${({ theme }) => theme.palette.foundation.primaryA};
  margin: 0;
`;

const SubTitle = styled.h3`
  font-size: 14px; font-weight: 600; line-height: 20px;
  color: ${({ theme }) => theme.palette.foundation.primaryA};
  margin: 0;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px 16px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldFull = styled(Field)`
  grid-column: 1 / -1;
`;

const Label = styled.label`
  font-size: 11px; font-weight: 400; line-height: 14px;
  color: ${({ theme }) => theme.palette.primitives.gray600};
`;

const Select = styled.select`
  font-size: 13px; font-weight: 400; line-height: 18px;
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.palette.primitives.gray200};
  border-radius: 8px;
  background: #ffffff;
`;

const Textarea = styled.textarea`
  font-size: 13px; font-weight: 400; line-height: 18px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.palette.primitives.gray200};
  border-radius: 8px;
  background: #ffffff;
  resize: vertical;
  font-family: inherit;
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const PrimaryButton = styled.button`
  font-size: 13px; font-weight: 600; line-height: 18px;
  background: ${({ theme }) => theme.palette.foundation.primaryB};
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  color: ${({ theme }) => theme.palette.foundation.primaryA};
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  font-size: 13px; font-weight: 600; line-height: 18px;
  background: #ffffff;
  border: 1px solid ${({ theme }) => theme.palette.primitives.gray300};
  border-radius: 8px;
  padding: 10px 20px;
  cursor: pointer;
`;

const Divider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.palette.primitives.gray100};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 320px;
  overflow-y: auto;
`;

const ListItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 16px;
  border: 1px solid ${({ theme }) => theme.palette.primitives.gray100};
  border-radius: 10px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Chip = styled.span<{ $tone: "type" | "resp" }>`
  font-size: 11px; font-weight: 400; line-height: 14px;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ theme, $tone }) =>
    $tone === "type"
      ? theme.palette.primitives.red50
      : theme.palette.primitives.gray100};
  color: ${({ theme }) => theme.palette.foundation.primaryA};
`;

const StatusSelect = styled.select`
  font-size: 11px; font-weight: 400; line-height: 14px;
  padding: 4px 8px;
  border: 1px solid ${({ theme }) => theme.palette.primitives.gray200};
  border-radius: 6px;
  background: #ffffff;
`;

const DeleteBtn = styled.button`
  font-size: 11px; font-weight: 400; line-height: 14px;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.palette.foundation.negative};
  cursor: pointer;
  margin-left: auto;
`;

const Memo = styled.div`
  font-size: 12px; font-weight: 400; line-height: 16px;
  color: ${({ theme }) => theme.palette.primitives.gray700};
`;

const Timestamp = styled.div`
  font-size: 11px; font-weight: 400; line-height: 14px;
  color: ${({ theme }) => theme.palette.primitives.gray500};
`;

const Empty = styled.div`
  font-size: 13px; font-weight: 400; line-height: 18px;
  color: ${({ theme }) => theme.palette.primitives.gray500};
  text-align: center;
  padding: 32px 0;
`;
